import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { selectIsLogoutProcessing } from '@app/app.reducer';
import { FormFields } from '@core/data-access/models/form/form-store.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { DynamicFieldOptionsTranslationFactory } from '@core/data-access/services/dynamic-form-field/dynamic-field-options-translation-factory.service';
import { DynamicFormFactory } from '@core/data-access/services/dynamic-form-field/dynamic-form-factory.service';
import { selectBuyerPerson } from '@core/features/buyer-information/store/buyer-information.selectors';
import { BuyerQuestionnaireFormState } from '@core/features/buyer-questionnaire/models/buyer-questionnaire-fom.model';
import {
  selectBuyerQuestionnaireDynamicFields,
  selectBuyerQuestionnaireFormFields
} from '@core/features/buyer-questionnaire/store/buyer-questionnaire.selectors';
import { questionnaireDynamicFormFieldMapper } from '@core/utils/dynamic-form-field/dynamic-form-field-mappers';
import { filterChangeFormFieldsPipe } from '@core/utils/form-fields/form-field-pipes';
import { Store } from '@ngrx/store';
import { isEmpty, isNil } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilKeyChanged,
  filter,
  map,
  merge,
  Observable,
  skip,
  tap,
  withLatestFrom
} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BuyerQuestionnaireFormFactory {
  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(Store);
  readonly #dynamicFormFactory = inject(DynamicFormFactory);
  readonly #dynamicFieldOptionsTranslationFactory = inject(DynamicFieldOptionsTranslationFactory);

  readonly #initialState = {
    dynamicFormFields: [],
    form: {
      formGroup: null,
      formName: FormName.BuyerQuestionnaire
    },
    person: null
  };

  #isFormStateCreated = false;

  readonly #resetFormState$ = new BehaviorSubject(false);

  readonly #buyerPerson$ = this.#store.select(selectBuyerPerson);
  readonly #buyerQuestionnaireFormFields$ = this.#store.select(selectBuyerQuestionnaireFormFields);

  readonly state = toSignal(this.state$(), {
    initialValue: this.#initialState
  });

  resetFormState() {
    this.#resetFormState$.next(true);
  }

  private state$(): Observable<BuyerQuestionnaireFormState> {
    return merge(this.createFormState$(), this.updateFormState$(), this.resetFormState$()).pipe(
      takeUntilDestroyed(this.#destroyRef)
    );
  }

  private createFormState$() {
    return combineLatest({
      resetFormState: this.#resetFormState$,
      person: this.#buyerPerson$,
      buyerQuestionnaireDynamicFields: this.#store.select(selectBuyerQuestionnaireDynamicFields)
    }).pipe(
      filter(({ buyerQuestionnaireDynamicFields }) => !isEmpty(buyerQuestionnaireDynamicFields)),
      filter(({ resetFormState }) => {
        const isResetTriggeredOnInitialize = !resetFormState;
        const recreateFormAfterUserLogoutOrAnonymousVoucherRelease = resetFormState && !this.#isFormStateCreated;

        return isResetTriggeredOnInitialize || recreateFormAfterUserLogoutOrAnonymousVoucherRelease;
      }),
      withLatestFrom(this.#buyerQuestionnaireFormFields$),
      map(([{ person, buyerQuestionnaireDynamicFields }, formFields]) => {
        const dynamicFormFields = questionnaireDynamicFormFieldMapper(buyerQuestionnaireDynamicFields);

        return {
          dynamicFormFields,
          form: {
            formName: FormName.BuyerQuestionnaire,
            formGroup: this.#dynamicFormFactory.createForm(dynamicFormFields, formFields)
          },
          person
        };
      }),
      tap(({ dynamicFormFields }) => {
        this.#dynamicFieldOptionsTranslationFactory.createTranslatedDynamicFieldOptions(
          FormName.BuyerQuestionnaire,
          dynamicFormFields
        );

        this.#isFormStateCreated = true;
      })
    );
  }

  private updateFormState$() {
    return merge(this.updateFormStateOnFormFieldsChange$(), this.updateFormStateOnBuyerPersonIdChange$());
  }

  private updateFormStateOnFormFieldsChange$() {
    return this.#buyerQuestionnaireFormFields$.pipe(
      filterChangeFormFieldsPipe(),
      filter((formFields) => !isEmpty(formFields) && this.#isFormStateCreated),
      map((formFields: FormFields) => {
        const { dynamicFormFields, form, person } = this.state();

        return {
          dynamicFormFields: this.#dynamicFormFactory.updateForm(dynamicFormFields, formFields, form.formGroup),
          form,
          person
        };
      })
    );
  }

  private updateFormStateOnBuyerPersonIdChange$() {
    const skipFirstLoad = 1;

    return this.#buyerPerson$.pipe(
      filter((person) => this.#isFormStateCreated && !isNil(person)),
      distinctUntilKeyChanged('id'),
      skip(skipFirstLoad),
      map((person) => {
        const { dynamicFormFields, form } = this.state();

        return {
          dynamicFormFields,
          form,
          person
        };
      })
    );
  }

  private resetFormState$() {
    return this.#resetFormState$.pipe(
      withLatestFrom(this.#store.select(selectIsLogoutProcessing)),
      filter(([resetFormState, isLogoutProcessing]) => resetFormState && !isLogoutProcessing),
      map(() => this.#initialState),
      tap(() => {
        this.#isFormStateCreated = false;

        this.#resetFormState$.next(false);
      })
    );
  }
}
